import { useEffect, useRef, useState } from "react";
import { Gift, X, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * GiftFinder — a discreet 2-question gift flow that opens a focused modal
 * (not pushy, not random) and ends at Amazon search with the Born Day
 * affiliate tag attached.
 *
 *   Q1: Who is this gift for? (recipient name, optional event picker)
 *   Q2: Any details? (age, theme, vibe, allergies, etc. — free text)
 *      → Opens Amazon search in a new tab with a smart query.
 *
 * Affiliate tag is enforced server-side via the existing /api/affiliate/link
 * helper so we never lose the attribution and never have to hardcode it
 * twice. If that endpoint is unreachable we fall back to a direct amazon.com
 * URL with `?tag=A3FWE4PYYD271O` appended.
 */
export default function GiftFinder() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [recipient, setRecipient] = useState("");
  const [eventCtx, setEventCtx] = useState("");
  const [details, setDetails] = useState("");
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/events");
        // Show only future events the user might want to shop for.
        const today = new Date().toISOString().slice(0, 10);
        setEvents((data || []).filter((e) => (e.date || "") >= today).slice(0, 8));
      } catch (e) {
        console.warn("[gift-finder] events fetch failed:", e?.message || e);
        setEvents([]);
      }
    })();
  }, [open]);

  const reset = () => {
    setStep(1); setRecipient(""); setEventCtx(""); setDetails("");
  };

  const close = () => { setOpen(false); reset(); };

  const goShop = async () => {
    setBusy(true);
    const query = [recipient && `gift for ${recipient}`, eventCtx, details]
      .filter(Boolean).join(" ").trim() || "gift ideas";
    let url = `https://www.amazon.com/s?k=${encodeURIComponent(query)}&tag=A3FWE4PYYD271O`;
    try {
      const { data } = await api.post("/affiliate/amazon-search", { query });
      if (data?.url) url = data.url;
    } catch (e) {
      // Server-side tagged URL failed — fall through to the locally-built
      // URL above which already carries the affiliate tag.
      console.warn("[gift-finder] tagged-url lookup failed:", e?.message || e);
    }
    setBusy(false);
    window.open(url, "_blank", "noopener,noreferrer");
    close();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(true)}
        className="bd-chip flex items-center gap-1.5"
        title="Find a gift"
        data-testid="gift-finder-trigger"
        aria-haspopup="dialog"
      >
        <Gift size={12} />
        <span className="hidden sm:inline">Find a gift</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 grid place-items-center p-4"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={close}
          data-testid="gift-finder-modal"
        >
          <div
            className="bd-surface p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>
                  Step {step} of 2
                </div>
                <h2 className="text-2xl font-bold" style={{ letterSpacing: "-0.02em" }}>
                  {step === 1 ? "Buying someone a gift?" : "Tell us a little about them"}
                </h2>
              </div>
              <button
                onClick={close}
                className="bd-btn bd-btn-ghost"
                style={{ padding: "0.4rem" }}
                aria-label="Close"
                data-testid="gift-finder-close"
              >
                <X size={14} />
              </button>
            </div>

            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="bd-label">Who&apos;s the gift for?</label>
                  <input
                    className="bd-input"
                    autoFocus
                    placeholder="e.g. Mom, Maya, Coach Dave"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    data-testid="gift-finder-recipient"
                  />
                </div>
                {events.length > 0 && (
                  <div>
                    <label className="bd-label">Or pick an event you have on the calendar</label>
                    <div className="flex flex-wrap gap-1.5">
                      {events.map((ev) => (
                        <button
                          key={ev.event_id}
                          type="button"
                          className="bd-chip"
                          style={
                            eventCtx === `${ev.title} (${ev.event_type})`
                              ? { background: "var(--primary)", color: "var(--primary-fg)", borderColor: "var(--primary)" }
                              : { fontSize: "0.7rem" }
                          }
                          onClick={() => {
                            const ctx = `${ev.title} (${ev.event_type})`;
                            setEventCtx(eventCtx === ctx ? "" : ctx);
                            if (!recipient && ev.person_name) setRecipient(ev.person_name);
                          }}
                          data-testid={`gift-finder-event-${ev.event_id}`}
                        >
                          {ev.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <button
                  className="bd-btn w-full"
                  disabled={!recipient.trim() && !eventCtx}
                  onClick={() => setStep(2)}
                  data-testid="gift-finder-next"
                >
                  Next <ArrowRight size={14} />
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="bd-label">
                    Any details? Age, interests, party theme, what they love…
                  </label>
                  <textarea
                    className="bd-input"
                    autoFocus
                    rows={4}
                    placeholder="e.g. 14 year old, loves volleyball, sleepover party theme"
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    data-testid="gift-finder-details"
                  />
                  <p className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>
                    The more you tell us, the sharper the Amazon search.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="bd-btn bd-btn-ghost flex-1"
                    onClick={() => setStep(1)}
                    data-testid="gift-finder-back"
                  >
                    Back
                  </button>
                  <button
                    className="bd-btn flex-[2]"
                    disabled={busy}
                    onClick={goShop}
                    data-testid="gift-finder-shop"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <><Gift size={14} /> Shop on Amazon</>}
                  </button>
                </div>
                <p className="text-[10px]" style={{ color: "var(--text-dim)", opacity: 0.7 }}>
                  Opens Amazon search in a new tab. We earn a small affiliate fee on qualifying purchases — costs you nothing extra and helps keep Born Day free.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
